import { useSelector } from 'react-redux';
import { getSalesProcessingFileStatuses } from 'redux/reducers/salesProcessing/selectors';
import {
	TFileStatuses,
	TProcessingTypes,
	TSalesProcessingRow,
} from 'redux/reducers/salesProcessing/types';
import { DATES, DATES_ID } from 'services/constants/dates-constants';
import { ETableStructureAlign, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { TUser } from 'redux/reducers/user/types';

export enum EFileStatus {
	NEW = 'New',
	NO_SALES = 'No Sales',
	PROCESSING = 'Processing',
	FAILED = 'Failed',
	PROCESSED = 'Processed',
	PENDING = 'Pending',
}

export enum EFileStatusId {
	NEW = 1,
	NO_SALES = 2,
	PROCESSING = 3,
	FAILED = 4,
	PROCESSED = 5,
	PENDING = 6,
}

export const tableStructureFunction = ({
	processingTypes,
	fileStatuses,
	usersList,
}: {
	processingTypes: TProcessingTypes[];
	fileStatuses: TFileStatuses[];
	usersList: TUser[];
}): TTableStructure<keyof TSalesProcessingRow>[] => {
	return [
		{
			name: 'EAS Order Id',
			selector: 'id' as keyof TSalesProcessingRow,
			show: false,
			sortable: true,
			filterable: false,
			filter: [['text', 'text']],
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Full Name',
			selector: 'fullName' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			searchable: true,
			colSpan: 1,
			align: ETableStructureAlign.LEFT,
			type: ETableStructureType.EM,
			value: (item: TSalesProcessingRow) => {
				return { id: item.emId, name: item.fullName };
			},
		},
		{
			name: 'File Name',
			selector: 'fileName' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			searchable: true,
			filter: [['text', 'text']],
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Sales Period',
			selector: 'salesPeriod' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: true,
			filter: [
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
			],
			colSpan: 1,
			type: ETableStructureType.DATE_MONTH,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Comments',
			selector: 'comments' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.TEXT,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Uploaded at',
			selector: 'uploadedAt' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: true,
			filter: [
				[DATES_ID.TODAY, DATES.TODAY],
				[DATES_ID.YESTERDAY, DATES.YESTERDAY],
				[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
				[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
				[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
				[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
				[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
				[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
				[DATES_ID.CUSTOM_RANGE, DATES.CUSTOM_RANGE],
			],
			colSpan: 1,
			type: ETableStructureType.DATE,
			align: ETableStructureAlign.CENTER,
		},
		{
			name: 'Uploaded by',
			selector: 'uploadByName' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'EAS comments',
			selector: 'easComments' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.TEXT,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Number of processed orders',
			selector: 'processedOrders' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Processing Type',
			selector: 'processingType' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: true,
			filter: processingTypes?.map((type: TProcessingTypes) => [type.name.toString(), type.name]),
			colSpan: 1,
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Processed at',
			selector: 'processedAt' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: true,
			colSpan: 1,
			filter: [
				[DATES_ID.TODAY, DATES.TODAY],
				[DATES_ID.YESTERDAY, DATES.YESTERDAY],
				[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
				[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
				[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
				[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
				[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
				[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
				[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
				[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
				[DATES_ID.CUSTOM_RANGE, DATES.CUSTOM_RANGE],
			],
			type: ETableStructureType.DATE,
			align: ETableStructureAlign.CENTER,
		},
		{
			name: 'CSV Fee Name',
			selector: 'csvFeeName' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.STRING,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'CSV Fee ID',
			selector: 'csvFee' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.RIGHT,
		},
		{
			name: 'Processed by',
			selector: 'processedBy' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: true,
			filter: usersList?.map((user: TUser) => [user.id, user.fullName]),
			colSpan: 1,
			type: ETableStructureType.USER,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Status',
			selector: 'fileStatus' as keyof TSalesProcessingRow,
			show: false,
			sortable: false,
			filterable: false,
			filter: fileStatuses?.map((status: TFileStatuses) => [status.name, status.name]),
			colors: [
				['New', 'primary.light'],
				['No Sales', 'success.light'],
				['Failed', 'error.light'],
				['Processed', 'success.light'],
				['Pending', 'warning.light'],
				['Processing', 'info.light'],
			],

			colSpan: 1,
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Status',
			selector: 'fileStatusId' as keyof TSalesProcessingRow,
			show: true,
			sortable: true,
			filterable: true,
			filter: fileStatuses?.map((status: TFileStatuses) => [status.id.toString(), status.name]),
			colors: [
				[
					fileStatuses?.find((status: TFileStatuses) => status.name === 'New')?.id.toString() ?? '',
					'primary.light',
				],
				[
					fileStatuses
						?.find((status: TFileStatuses) => status.name === 'No Sales')
						?.id.toString() ?? '',
					'success.light',
				],
				[
					fileStatuses?.find((status: TFileStatuses) => status.name === 'Failed')?.id.toString() ??
						'',
					'error.light',
				],
				[
					fileStatuses
						?.find((status: TFileStatuses) => status.name === 'Processed')
						?.id.toString() ?? '',
					'success.light',
				],
				[
					fileStatuses?.find((status: TFileStatuses) => status.name === 'Pending')?.id.toString() ??
						'',
					'warning.light',
				],
				[
					fileStatuses
						?.find((status: TFileStatuses) => status.name === 'Processing')
						?.id.toString() ?? '',
					'info.light',
				],
			],

			colSpan: 1,
			type: ETableStructureType.STATUS,
			align: ETableStructureAlign.LEFT,
		},
		{
			name: 'Actions',
			selector: 'actions' as keyof TSalesProcessingRow,
			show: true,
			sortable: false,
			filterable: false,
			colSpan: 1,
			type: ETableStructureType.BUTTONS,
			align: ETableStructureAlign.LEFT,
			buttons: [{ name: 'Process File' }],
			buttonFilter: (item: TSalesProcessingRow) => {
				const buttons = [];
				buttons.push({ name: 'Process File' });
				if (item.fileName !== null) {
					buttons.push({ name: 'Download File' });
				}
				return buttons;
			},
		},
	];
};
